import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, Link } from "react-router-dom";
import clsx from "clsx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Typography from "@material-ui/core/Typography";
import MenuIcon from "@material-ui/icons/Menu";
import MenuOpen from "@material-ui/icons/MenuOpen";
import NavigateNext from "@material-ui/icons/NavigateNext";

import componentStyles from "assets/theme/components/sidebar.js";
import { IsSmall } from "utils/is-mobile";

const useStyles = makeStyles(componentStyles);
const useCustomStyles = makeStyles({
  menuOption: {
    display: "flex",
    alignItems: "center",
    fontSize: "1rem",
  },
  treeConnector: {
    borderLeft: "2px solid rgba(0,0,0,0.08)",
  },
  treeCollapseIcon: {
    transition: "transform 200ms ease",
  },
  treeCollapseIconActive: {
    transform: "rotate(90deg)",
  },
});

export default function Sidebar({ routes, isOpen, toggleSidebar, onClose }) {
  const isMobile = IsSmall();
  const classes = { ...useStyles(), ...useCustomStyles() };
  const location = useLocation();
  const [state, setState] = useState({});

  const smallSidebar = isMobile ? false : isOpen;

  const mergedRoutes = useMemo(
    () => (Array.isArray(routes) ? routes : []),
    [routes]
  );

  const getTreeInitialState = useCallback(
    (nodes, parentKey = "") => {
      if (!nodes || !Array.isArray(nodes)) {
        return false;
      }

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.invisible) continue;

        const children = n.views;
        const currentKey = `${parentKey}_${n.path || n.name || i}`;

        if (children && Array.isArray(children) && children.length > 0) {
          if (getTreeInitialState(children, currentKey)) {
            return true;
          }
        }

        if (
          typeof n.path === "string" &&
          location.pathname.indexOf(n.path) !== -1
        ) {
          return true;
        }
      }

      return false;
    },
    [location.pathname]
  );

  const getTreeStates = useCallback(
    (nodes, parentKey = "") => {
      let initialState = {};
      if (!nodes || !Array.isArray(nodes)) {
        return initialState;
      }

      nodes.forEach((n, index) => {
        if (n.invisible) return;

        const children = n.views;
        const currentKey = `${parentKey}_${n.path || n.name || index}`;

        if (Array.isArray(children) && children.length > 0) {
          const hasVisibleChildren = children.some((child) => !child.invisible);
          if (hasVisibleChildren) {
            initialState[currentKey] = getTreeInitialState(
              children,
              currentKey
            );
            initialState = {
              ...getTreeStates(children, currentKey),
              ...initialState,
            };
          }
        }
      });
      return initialState;
    },
    [getTreeInitialState]
  );

  useEffect(() => {
    const treeState = getTreeStates(mergedRoutes);
    setState({ ...treeState });
  }, [getTreeStates, location.pathname, mergedRoutes]);

  const renderTree = (routes, depth = 0, parentKey = "") => {
    if (!routes || !Array.isArray(routes)) {
      return null;
    }

    const indentation = smallSidebar ? 0 : 8 + depth * 16;
    return routes.map((prop, key) => {
      if (prop.redirect || prop.invisible) {
        return null;
      } else if (prop.divider) {
        return <Divider key={`divider_${key}`} />;
      } else if (prop.title) {
        if (smallSidebar) {
          return null;
        }

        return (
          <Typography
            key={`title_${key}`}
            variant="caption"
            classes={{ root: classes.title }}
          >
            {prop.title}
          </Typography>
        );
      }

      const currentKey = `${parentKey}_${prop.path || prop.name || key}`;
      const hasVisibleChildren =
        Array.isArray(prop.views) &&
        prop.views.length > 0 &&
        prop.views.some((view) => !view.invisible);
      const isTreeNode =
        hasVisibleChildren || (prop.collapse && hasVisibleChildren);

      const textContent = (
        <>
          <Box
            minWidth="2.25rem"
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            style={{ marginLeft: depth > 0 ? indentation : 0 }}
          >
            {prop.icon && (
              <Box
                component={prop.icon}
                width="1.25rem!important"
                height="1.25rem!important"
                className={
                  prop.iconColor ? classes["text" + prop.iconColor] : undefined
                }
              />
            )}
          </Box>
          {!smallSidebar && (
            <Box component="span">{prop.name || prop.defaultName}</Box>
          )}
        </>
      );

      if (isTreeNode) {
        const nodeKey = currentKey;
        const nextState = { ...state, [nodeKey]: !state[nodeKey] };
        const selectedTree =
          typeof prop.path === "string" && location.pathname === prop.path;
        return (
          <div
            key={nodeKey}
            className={depth > 0 ? classes.treeConnector : undefined}
          >
            <ListItem
              component={prop.path ? Link : "a"}
              to={prop.path}
              href={prop.path ? undefined : "#mui"}
              onClick={(e) => {
                if (!prop.path) {
                  e.preventDefault();
                }
                setState(nextState);
              }}
              style={{ paddingLeft: depth > 0 ? indentation : undefined }}
              classes={{
                root: clsx(classes.listItemRoot, classes.menuOption),
                selected: classes.listItemSelected,
              }}
              selected={selectedTree}
            >
              {textContent}
              {smallSidebar ? null : (
                <Box
                  component={NavigateNext}
                  marginLeft="auto"
                  width="1rem!important"
                  height="1rem!important"
                  className={clsx(classes.treeCollapseIcon, {
                    [classes.treeCollapseIconActive]: state[nodeKey],
                  })}
                />
              )}
            </ListItem>
            <Collapse
              in={!!state[nodeKey]}
              unmountOnExit
              className={classes.collapseRoot}
            >
              <List classes={{ root: classes.listRootCollapse }} disablePadding>
                {renderTree(prop.views, depth + 1, nodeKey)}
              </List>
            </Collapse>
          </div>
        );
      }

      if (prop.href) {
        return (
          <ListItem
            key={`href_${currentKey}`}
            component={"a"}
            href={prop.href}
            onClick={isOpen && onClose}
            style={{ paddingLeft: indentation }}
            classes={{
              root:
                classes.listItemRoot +
                (prop.upgradeToPro
                  ? " " + classes.listItemRootUpgradeToPro
                  : ""),
              selected: classes.listItemSelected,
            }}
            target="_blank"
            selected={prop.upgradeToPro === true}
          >
            {textContent}
          </ListItem>
        );
      }

      const to = prop.layout ? prop.layout + prop.path : prop.path;
      const selected = prop.layout
        ? location.pathname === to || prop.upgradeToPro === true
        : location.pathname === prop.path || prop.upgradeToPro === true;

      return (
        <ListItem
          key={`link_${currentKey}`}
          component={Link}
          onClick={isOpen ? onClose : undefined}
          to={to}
          style={{ paddingLeft: indentation }}
          classes={{
            root:
              classes.listItemRoot +
              (prop.upgradeToPro ? " " + classes.listItemRootUpgradeToPro : ""),
            selected: classes.listItemSelected,
          }}
          selected={selected}
        >
          {textContent}
        </ListItem>
      );
    });
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      anchor="left"
      classes={{
        paper: clsx({
          [classes.drawerDockedMiniActive]: smallSidebar,
        }),
        docked: clsx({
          [classes.drawerPaperMiniActive]: smallSidebar,
        }),
      }}
      open={isOpen}
      onClose={onClose}
    >
      <PerfectScrollbar>
        <Box
          paddingLeft={smallSidebar ? "0" : "0.5rem"}
          display="flex"
          justifyContent={smallSidebar ? "center" : "flex-start"}
        >
          <IconButton onClick={toggleSidebar}>
            <Box
              component={!smallSidebar ? MenuOpen : MenuIcon}
              width="1.5rem!important"
              height="1.5rem!important"
            />
          </IconButton>
        </Box>
        <List classes={{ root: classes.listRoot }}>
          {renderTree(mergedRoutes)}
        </List>
      </PerfectScrollbar>
    </Drawer>
  );
}

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      layout: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.elementType,
      collapse: PropTypes.bool,
      views: PropTypes.array,
      href: PropTypes.string,
      title: PropTypes.string,
      divider: PropTypes.bool,
      invisible: PropTypes.bool,
      redirect: PropTypes.bool,
      upgradeToPro: PropTypes.bool,
      iconColor: PropTypes.string,
      multiStates: PropTypes.arrayOf(PropTypes.string),
      state: PropTypes.string,
    })
  ),
  isOpen: PropTypes.bool,
  toggleSidebar: PropTypes.func,
  onClose: PropTypes.func,
};

Sidebar.defaultProps = {
  routes: [],
};
